// Client-side service for journal prompt generation
// All OpenAI API calls go through our secure server proxy

// Configuration constants
const MAX_RECENT_QUESTIONS = 10;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

// Default GPT model to use
const DEFAULT_MODEL = 'gpt-4o-mini';

// Keep track of recent questions to avoid repeating
const recentQuestions: string[] = [];

// Helper utility for async retries
const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = MAX_RETRY_ATTEMPTS,
  delayMs: number = RETRY_DELAY_MS
): Promise<T> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't delay on the final attempt
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  throw lastError || new Error('Failed after multiple retries');
};

// Function to generate journal enhancement prompts
export const generateJournalPrompts = async (
  journalText: string,
  location?: string,
  minWordCount: number = 50,
  seed: number = Math.floor(Math.random() * 1000),
  retries: number = MAX_RETRY_ATTEMPTS
): Promise<string[]> => {
  // Early validation
  if (!journalText || journalText.trim() === '') {
    return ['What motivated you to start journaling today?'];
  }

  // Clean text inputs
  const cleanJournalText = journalText.replace(/["\\]/g, '').trim();
  const cleanLocation = location ? location.replace(/["\\]/g, '').trim() : undefined;

  return withRetry(async () => {
    try {
      // Natural conversational prompt that makes the AI genuinely curious
      const prompt = `
I just told you this:
"${cleanJournalText}"
${cleanLocation ? `\nI'm in: ${cleanLocation}` : ''}

You're my friend and you just heard me vent. Ask me ONE natural question that shows you were listening and makes me think deeper about what I just told you.

Use my exact words and be genuinely curious about my situation.

Respond ONLY with the question - no explanation.`;

      // Call our secure server proxy instead of OpenAI directly
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: DEFAULT_MODEL,
          messages: [
            {
              role: "system",
              content: "You are a caring friend who just heard someone share something personal. Ask them ONE natural question that shows you were listening and makes them think deeper. Use their exact words and be genuinely curious about their situation."
            },
            { role: "user", content: prompt }
          ],
          temperature: 0.8,
          max_tokens: 50,
          seed: seed,
          presence_penalty: 0.6,
          frequency_penalty: 0.3
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const text = data.choices?.[0]?.message?.content || '';

      // Clean up the response
      const cleanedQuestion = text
        .replace(/^["']|["']$/g, '')
        .replace(/^(here's a question:|question:|follow-up:|follow up:)/i, '')
        .trim();

      // Make sure it ends with a question mark
      const finalQuestion = cleanedQuestion.endsWith('?')
        ? cleanedQuestion
        : cleanedQuestion + '?';

      // Validate the question quality
      if (finalQuestion.length < 10 || finalQuestion.toLowerCase().includes('experience with rather')) {
        throw new Error('Generated question quality is poor');
      }

      // Store the question in the recent questions list
      addToRecentQuestions(finalQuestion);

      return [finalQuestion];
    } catch (error) {
      console.warn('[mania] follow-up generation failed', error);
      throw error;
    }
  }, retries);
};

function addToRecentQuestions(question: string): void {
  // Add to recent questions and keep only the most recent ones
  recentQuestions.unshift(question);

  if (recentQuestions.length > MAX_RECENT_QUESTIONS) {
    recentQuestions.pop();
  }
}

// Create a service object with all the exported functions
const gptService = {
  generateJournalPrompts
};

export default gptService;
